import apiClient, { buildAuthHeaders } from 'services/apiClient';
import { BASE_URL } from 'BaseURL';

export const CLIENT_OPERATING_ANALYSIS_TYPES = [
    'all_vehicles',
    'per_vehicle',
    'per_vehicle_summary',
    'driving_behavior',
];

const ANALYSIS_ENDPOINTS = {
    all_vehicles: 'all-vehicles',
    per_vehicle: 'per-vehicle',
    per_vehicle_summary: 'per-vehicle-summary',
    driving_behavior: 'driving-behavior',
};

const ACTIVE_REFRESH_STATUSES = new Set(['pending', 'running', 'refresh_pending', 'refresh_requested']);
const TERMINAL_REFRESH_STATUSES = new Set(['ready', 'failed', 'timed_out']);

const authHeaders = (token) => ({
    'Content-Type': 'application/json',
    ...buildAuthHeaders(token),
});

const clientOperatingAnalysisUrl = (analysisType) => {
    const endpoint = ANALYSIS_ENDPOINTS[analysisType];
    if (!endpoint) {
        throw new Error(`Unsupported operating analysis type: ${analysisType}`);
    }
    return `${BASE_URL}shiftware/api/v1/client-operating-analysis/${endpoint}/`;
};

const refreshUrl = () => `${BASE_URL}shiftware/api/v1/client-operating-analysis/refresh/`;

export const refreshStatusUrl = (jobId) => (
    `${BASE_URL}shiftware/api/v1/client-operating-analysis/refresh/${jobId}/`
);

export const getAnalysisLabel = (analysisType) => {
    if (analysisType === 'all_vehicles') return 'Fleet summary';
    if (analysisType === 'per_vehicle') return 'Per vehicle per date';
    if (analysisType === 'per_vehicle_summary') return 'Per vehicle summary';
    if (analysisType === 'driving_behavior') return 'Driving behavior';
    return analysisType;
};

export const isRefreshActive = (status) => ACTIVE_REFRESH_STATUSES.has(String(status || ''));

export const isRefreshTerminal = (status) => TERMINAL_REFRESH_STATUSES.has(String(status || ''));

const normalizeTiming = (timing = {}) => ({
    startedAt: timing.started_at || timing.startedAt || null,
    elapsedSeconds: timing.elapsed_seconds ?? timing.elapsedSeconds ?? null,
    estimatedSecondsRemaining: timing.estimated_seconds_remaining ?? timing.estimatedSecondsRemaining ?? null,
    estimatedDurationSeconds: timing.estimated_duration_seconds ?? timing.estimatedDurationSeconds ?? null,
    estimateSource: timing.estimate_source ?? timing.estimateSource ?? null,
    timeoutSeconds: timing.timeout_seconds ?? timing.timeoutSeconds ?? null,
    timeoutSecondsRemaining: timing.timeout_seconds_remaining ?? timing.timeoutSecondsRemaining ?? null,
});

const normalizeVehicleModelValues = (vehicleModels = []) => (
    [...new Set((vehicleModels || []).map((value) => String(value || '').trim()).filter(Boolean))]
);

export const normalizeVehicleModelFilter = (filter) => {
    if (!filter || typeof filter !== 'object') return null;

    const options = Array.isArray(filter.options)
        ? filter.options
            .map((option) => ({
                value: String(option?.value || '').trim(),
                label: String(option?.label || option?.value || '').trim(),
                vehicleCount: Number(option?.vehicle_count ?? option?.vehicleCount ?? 0),
            }))
            .filter((option) => option.value)
        : [];

    return {
        parameter: filter.parameter || 'vehicle_model',
        selectionMode: filter.selection_mode || filter.selectionMode || 'multiple',
        isAvailable: Boolean(filter.is_available ?? filter.isAvailable),
        selected: normalizeVehicleModelValues(filter.selected),
        totalVehicleCount: Number(filter.total_vehicle_count ?? filter.totalVehicleCount ?? 0),
        selectedVehicleCount: Number(filter.selected_vehicle_count ?? filter.selectedVehicleCount ?? 0),
        options,
    };
};

export const normalizeCachedAnalysisResponse = (analysisType, response) => {
    const payload = response.data || {};
    const cacheStatus = payload.cache?.cache_status || payload.cache_status || 'unknown';

    if (response.status === 200 && cacheStatus === 'ready') {
        return {
            analysisType,
            httpStatus: response.status,
            cacheStatus: 'ready',
            payload,
            columns: payload.columns || [],
            rows: payload.rows || [],
            isStale: Boolean(payload.freshness?.is_stale || payload.cache?.is_stale),
            refreshedAt: payload.freshness?.refreshed_at || payload.cache?.refreshed_at || null,
            expiresAt: payload.freshness?.expires_at || payload.cache?.expires_at || null,
            rowCount: payload.cache?.row_count ?? payload.rows?.length ?? 0,
            jobId: payload.cache?.job_id || payload.cache?.snapshot_id || null,
            statementName: payload.statement_name,
            scope: payload.scope,
            snapshot: payload.snapshot || payload.cache || null,
            timing: normalizeTiming(payload.timing),
            vehicleModelFilter: normalizeVehicleModelFilter(payload.filters?.vehicle_models),
            detail: payload.detail || '',
        };
    }

    return {
        analysisType,
        httpStatus: response.status,
        cacheStatus,
        payload,
        columns: [],
        rows: [],
        isStale: false,
        refreshedAt: null,
        expiresAt: null,
        rowCount: 0,
        jobId: payload.job_id || payload.snapshot?.id || null,
        statementName: payload.statement_name,
        scope: payload.scope,
        snapshot: payload.snapshot,
        timing: normalizeTiming(payload.timing),
        vehicleModelFilter: normalizeVehicleModelFilter(payload.filters?.vehicle_models),
        detail: payload.detail || '',
        error: payload.error || '',
        errorCode: payload.error_code || '',
    };
};

const isExpectedCachedAnalysisStatus = (response) => {
    if ([200, 202].includes(response.status)) return true;
    const cacheStatus = response?.data?.cache?.cache_status || response?.data?.cache_status;
    return response.status === 404 && cacheStatus === 'cache_miss';
};

export const normalizeRefreshJob = (job) => ({
    jobId: job.job_id || job.snapshot?.id || job.id,
    cacheStatus: job.cache_status || job.snapshot?.status || 'unknown',
    analysisType: job.analysis_type,
    statementName: job.statement_name,
    scope: job.scope,
    snapshot: job.snapshot,
    timing: normalizeTiming(job.timing),
    error: job.error || '',
    errorCode: job.error_code || '',
});

const formatApiErrorData = (data) => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return '';

    const messages = Object.entries(data).flatMap(([key, value]) => {
        if (key === 'detail' || key === 'error' || key === 'message') return [];
        if (Array.isArray(value)) {
            return value.map((item) => `${key}: ${item}`);
        }
        if (value && typeof value === 'object') {
            return [`${key}: ${JSON.stringify(value)}`];
        }
        if (value !== undefined && value !== null && value !== '') {
            return [`${key}: ${value}`];
        }
        return [];
    });

    return messages.join(' ');
};

export const buildApiError = (response) => {
    const detail = response?.data?.detail
        || response?.data?.error
        || response?.data?.message
        || formatApiErrorData(response?.data)
        || response?.statusText
        || 'Request failed.';
    const error = new Error(detail);
    error.status = response?.status;
    error.data = response?.data;
    return error;
};

export const getCachedClientOperatingAnalysis = async ({
    analysisType,
    client,
    dateFrom,
    dateTo,
    vehicleModels = [],
    token,
}) => {
    const normalizedVehicleModels = normalizeVehicleModelValues(vehicleModels);
    const response = await apiClient.get(clientOperatingAnalysisUrl(analysisType), {
        headers: authHeaders(token),
        params: {
            client,
            date_from: dateFrom,
            date_to: dateTo,
            ...(normalizedVehicleModels.length > 0 ? { vehicle_model: normalizedVehicleModels } : {}),
        },
        paramsSerializer: { indexes: null },
        validateStatus: (status) => status < 500,
    });

    if (isExpectedCachedAnalysisStatus(response)) {
        return normalizeCachedAnalysisResponse(analysisType, response);
    }

    throw buildApiError(response);
};

export const requestClientOperatingAnalysisRefresh = async ({
    client,
    dateFrom,
    dateTo,
    analysisTypes,
    token,
    force = false,
}) => {
    const response = await apiClient.post(
        refreshUrl(),
        {
            client,
            date_from: dateFrom,
            date_to: dateTo,
            analysis_types: analysisTypes,
            force,
        },
        {
            headers: authHeaders(token),
            validateStatus: (status) => status < 500,
        }
    );

    if (response.status !== 202) {
        throw buildApiError(response);
    }

    return {
        ...response.data,
        jobs: (response.data?.jobs || []).map(normalizeRefreshJob),
    };
};

export const getClientOperatingAnalysisRefreshStatus = async ({ jobId, token }) => {
    const response = await apiClient.get(refreshStatusUrl(jobId), {
        headers: authHeaders(token),
        validateStatus: (status) => status < 500,
    });

    if (response.status !== 200) {
        throw buildApiError(response);
    }

    return normalizeRefreshJob(response.data || {});
};
